import { getUserData } from './API';
import { isDefined } from './util';
import { getWCPMGradePR } from './readerCharacteristics';
import { calculateMostRecentScores } from '../components/Reader/ReaderLibrary';
import { getAudioDuration } from './SpeechStream';
import { isSpanishStory } from '../components/Reader/ReaderUtils';

/**
 * This service handles the releveling logic for assessment activities when orfMode is true.
 * It determines when students should receive easier or harder passages based on their reading performance.
 */

/**
 * Helper function to check if a story is eligible for downleveling based on story grade and student grade
 * @param {boolean} shouldUseAOS Flag indicating whether Adaptive Online System is in use
 * @param {Object} story The current story object
 * @param {number} studentGrade The student's actual grade level
 * @param {number} storyGrade The grade level of the current story
 * @returns {boolean} Whether the story is eligible for downleveling
 */
export function isStoryEligibleForDownlevel(shouldUseAOS, story, studentGrade, storyGrade) {
  if (
    shouldUseAOS &&
    isDefined(story.levelingDelta) &&
    story.levelingDelta > 0
  ) {
    // We are in AOS and the story has been upleveled, so don't downlevel
    return false;
  }

  if (
    !shouldUseAOS &&
    storyGrade > studentGrade
  ) {
    // We are not in AOS and the story has been upleveled, so don't downlevel
    return false;
  }
  
  return true;
}

/**
 * Determines if downlevel criteria are met based on student reading performance
 * @param {number} wordsCorrect The number of words read correctly
 * @param {number} studentGrade The student's actual grade level
 * @param {number} storyGrade The grade level of the current story
 * @param {boolean} isCalibration Whether this is a calibration assignment
 * @param {boolean} shouldUseAOS Flag indicating whether Adaptive Online System is in use
 * @param {Object} story The current story object
 * @returns {boolean} Whether the downlevel criteria are met
 */
export function downLevelWordsCorrectCriteriaMet(wordsCorrect, studentGrade, storyGrade, isCalibration, shouldUseAOS, story) {
  if (wordsCorrect >= 8) {
    // If the student has read at least 8 words correctly, don't downlevel
    return false;
  }

  return isStoryEligibleForDownlevel(shouldUseAOS, story, studentGrade, storyGrade);
}

/**
 * Determines if downlevel criteria are met based on student reading accuracy
 * @param {number} accuracySoFar The accuracy percentage so far
 * @param {number} studentGrade The student's actual grade level
 * @param {number} storyGrade The grade level of the current story
 * @param {boolean} shouldUseAOS Flag indicating whether Adaptive Online System is in use
 * @param {Object} story The current story object
 * @returns {boolean} Whether the downlevel criteria are met
 */
export function downLevelAccuracyCriteriaMet(accuracySoFar, studentGrade, storyGrade, shouldUseAOS, story) {
  if (accuracySoFar >= 60) {
    // If the student has read at least 60% of the words correctly, don't downlevel
    return false;
  }

  return isStoryEligibleForDownlevel(shouldUseAOS, story, studentGrade, storyGrade);
}

/**
 * Determines if uplevel criteria are met
 * @param {number} storyGrade The grade level of the current story
 * @param {number} studentGrade The student's actual grade level
 * @param {number} maxUplevelGrade The maximum grade level for upleveling
 * @param {number} wcpmPR The Words Correct Per Minute percentile rank
 * @param {boolean} shouldUseAOS Flag indicating whether Adaptive Online System is in use
 * @param {Object} story The current story object
 * @returns {boolean} Whether the uplevel criteria are met
 */
export function upLevelCriteriaMet(storyGrade, studentGrade, maxUplevelGrade, wcpmPR, shouldUseAOS, story) {
  if (story.maxGradeLevel || wcpmPR < 95) {
    // If the story is at the max grade level or the wcpmPR is less than 95, don't uplevel
    return false;
  }

  if (
    shouldUseAOS &&
    isDefined(story.levelingDelta) &&
    story.levelingDelta < 0
  ) {
    // We are in AOS and the story has been downleveled, so don't uplevel
    return false;
  }

  if (
    !shouldUseAOS &&
    (
      storyGrade >= maxUplevelGrade ||
      storyGrade < studentGrade
    )
  ) {
    // We are not in AOS and the story is at the max grade level or the story has been downleveled, so don't uplevel
    return false;
  }
  
  return true;
}

/**
 * Determines if downlevel criteria are met for RAN stories based on number of words read
 * @param {number} currentWordIndex The current word index in the phrase
 * @returns {boolean} Whether the downlevel criteria are met for RAN stories
 */
export function ranDownLevelCriteriaMet(currentWordIndex) {
  // Downlevel if student has read 3 or fewer words
  return currentWordIndex <= 3;
}